import { FontFamily } from '@euler/components/typography/fonts';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import React, { createContext, memo, useContext, useMemo } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { useInlineFormatter } from './InlineFormatter';

export function isTableSeparator(line: string): boolean {
  return /^\|[\s:*-]+\|/.test(line.trim());
}

export function parseTableRow(line: string): string[] {
  return line
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map(cell => cell.trim());
}

export type ColumnAlign = 'left' | 'center' | 'right';

/** Read per-column alignment from a markdown separator row: `:---:` → center,
 * `---:` → right, else left. */
export function parseTableAlign(line: string): ColumnAlign[] {
  return parseTableRow(line).map(spec => {
    const s = spec.trim();
    const left = s.startsWith(':');
    const right = s.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

const ALIGN_ITEMS: Record<ColumnAlign, 'flex-start' | 'center' | 'flex-end'> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

/** Approximate on-screen width of a cell in ems — what it costs at the
 * table's font size. Measured against NotoSans at 12px: a CJK glyph is one
 * full em, a digit or lowercase letter about 0.6, and capitals run wider
 * (`W` is 0.92), which is why plates like 新LW9492L wrapped when every
 * latin character was counted the same. Rounded up rather than down: a
 * column a few px too wide costs nothing, one a few px too narrow wraps. */
function textEm(s: string): number {
  let n = 0;
  for (const ch of s) {
    if (ch.charCodeAt(0) > 0xff) n += 1;
    else if (ch >= 'A' && ch <= 'Z') n += 0.75;
    else n += 0.6;
  }
  return n;
}

/** One CJK char of breathing room added to a header's width when sizing its
 * column, so the header text isn't cramped. */
const HEADER_BREATH_EM = 1;

/** A column stops widening past 13 CJK chars — one essay-length cell
 * shouldn't push every other column off-screen; it wraps instead. */
const MAX_COL_EM = 13;

/**
 * Whether a table too wide for its host may scroll sideways instead of
 * wrapping every cell. A host that cannot take a nested horizontal scroller
 * turns this off: the chat conversation's inverted FlashList blanks every
 * iOS cell after one (see `flows/chat/CLAUDE.md`), so its bubbles keep the
 * wrapping table.
 */
export const ScrollableTableContext = createContext(true);

export const MarkdownTable = memo(
  ({
    rows,
    align,
    zebra = true,
    compact,
    footer,
  }: {
    rows: string[][];
    align?: ColumnAlign[];
    /** Alternate rows shaded; on by default. */
    zebra?: boolean;
    /** Tighter rows. */
    compact?: boolean;
    /** A last row set apart, e.g. totals. */
    footer?: string[];
  }) => {
    const { rpx } = useResponsiveContext();
    const renderInline = useInlineFormatter();
    const cellPadding = rpx(compact ? 8 : 10);
    const cellPaddingVertical = rpx(compact ? 3 : 6);
    const fontSize = rpx(12);
    const scrollable = useContext(ScrollableTableContext);
    const alignItemsFor = (ci: number) => ALIGN_ITEMS[align?.[ci] ?? 'left'];
    const s = useMemo(
      () =>
        StyleSheet.create({
          table: {
            flexGrow: 1,
            borderRadius: rpx(8),
            borderWidth: 1,
            borderColor: '#E2E8F0',
            overflow: 'hidden',
            marginVertical: rpx(6),
          },
          tableHeaderRow: {
            flexDirection: 'row',
            backgroundColor: '#F1F5F9',
            borderBottomWidth: 1,
            borderBottomColor: '#E2E8F0',
          },
          tableRow: {
            flexDirection: 'row',
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: '#E2E8F0',
          },
          tableRowAlt: {
            backgroundColor: '#F8FAFC',
          },
          scroller: {
            flexGrow: 1,
          },
          tableCell: {
            paddingHorizontal: cellPadding,
            paddingVertical: cellPaddingVertical,
          },
          tableFooterRow: {
            flexDirection: 'row',
            backgroundColor: '#F1F5F9',
            borderTopWidth: 1,
            borderTopColor: '#E2E8F0',
          },
          tableHeaderCell: {
            paddingVertical: rpx(8),
          },
          tableHeaderText: {
            fontSize,
            fontFamily: FontFamily.NotoSans.Bold,
            color: '#334155',
          },
          tableCellText: {
            fontSize,
            lineHeight: rpx(18),
            fontFamily: FontFamily.NotoSans.Regular,
            color: '#475569',
          },
        }),
      [rpx, cellPadding, cellPaddingVertical, fontSize],
    );

    // Width each column proportionally to its widest cell so short columns
    // (a time, a 2-char name) don't claim equal width and force long columns to
    // wrap. The column's own HEADER text counts toward that width too (+ one
    // char of breathing room) so a long content column can't starve a short
    // one into a wrapped header.
    //
    // That width is also the column's `minWidth`, which is what makes the
    // table scroll: a table whose columns add up to less than the bubble
    // still fills it (the weights share the slack via `flexGrow`), and one
    // that doesn't fit keeps every column readable and runs off the right
    // edge into the horizontal scroller instead of wrapping into a grid of
    // two-line cells.
    const colFlex = useMemo(() => {
      const headerRow = rows[0] ?? [];
      const body = footer ? [...rows.slice(1), footer] : rows.slice(1);
      const cols = rows.reduce((max, r) => Math.max(max, r.length), 0);
      return Array.from({ length: cols }, (_, c) => {
        let em = textEm(headerRow[c] ?? '') + HEADER_BREATH_EM;
        for (const row of body) {
          em = Math.max(em, textEm(row[c] ?? ''));
        }
        if (!scrollable) return { flexGrow: em, flexShrink: 1, flexBasis: 0 };
        return {
          flexGrow: em,
          flexShrink: 0,
          flexBasis: 0,
          minWidth: Math.min(em, MAX_COL_EM) * fontSize + cellPadding * 2,
        };
      });
    }, [rows, footer, scrollable, fontSize, cellPadding]);

    if (rows.length === 0) return null;
    const header = rows[0];
    const body = rows.slice(1);

    const table = (
      <View style={s.table}>
        <View style={s.tableHeaderRow}>
          {header.map((cell, ci) => (
            <View
              key={ci}
              style={[
                s.tableCell,
                s.tableHeaderCell,
                colFlex[ci],
                { alignItems: alignItemsFor(ci) },
              ]}
            >
              <Text
                numberOfLines={1}
                style={[
                  s.tableHeaderText,
                  { textAlign: align?.[ci] ?? 'left' },
                ]}
              >
                {cell}
              </Text>
            </View>
          ))}
        </View>
        {body.map((row, ri) => (
          <View
            key={ri}
            style={[s.tableRow, zebra && ri % 2 === 1 && s.tableRowAlt]}
          >
            {row.map((cell, ci) => (
              <View
                key={ci}
                style={[
                  s.tableCell,
                  colFlex[ci],
                  { alignItems: alignItemsFor(ci) },
                ]}
              >
                <Text
                  style={[
                    s.tableCellText,
                    { textAlign: align?.[ci] ?? 'left' },
                  ]}
                >
                  {renderInline(cell)}
                </Text>
              </View>
            ))}
          </View>
        ))}
        {footer ? (
          <View style={s.tableFooterRow}>
            {footer.map((cell, ci) => (
              <View
                key={ci}
                style={[
                  s.tableCell,
                  colFlex[ci],
                  { alignItems: alignItemsFor(ci) },
                ]}
              >
                <Text
                  numberOfLines={1}
                  style={[
                    s.tableHeaderText,
                    { textAlign: align?.[ci] ?? 'left' },
                  ]}
                >
                  {cell}
                </Text>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    );

    if (!scrollable) return table;
    return (
      // Core RN's scroller, not gesture-handler's: the bubble's own
      // long-press has to keep winning over a child that only scrolls.
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={s.scroller}
      >
        {table}
      </ScrollView>
    );
  },
);
